import Spinner from '@v2/shared/components/Spinner';
import { cn } from '@v2/shared/lib/utils';
import ProductReviewCard from './ProductReviewCard';
import ReviewCard from './ReviewCard';
import ReviewSkeleton from './ReviewSkeleton';
import { ReviewPage, ReviewSource, Tab } from './types';

type Props = {
  tab: Tab;
  page: ReviewPage | null;
  isLoading: boolean;
  hasFailed: boolean;
  isFiltered: boolean;
  perPage: number;
  source: ReviewSource;
  onClearFilters: () => void;
};

const MESSAGE_CLASS = 'rounded-xl border border-solid border-slate-200 bg-white p-6 text-sm text-slate-600';

/**
 * The reviews themselves, and every loading state they can be in.
 *
 * There are two, and the difference matters. Reviews we already have stay on screen and
 * dim while the next set is fetched, so turning a page does not blank the section a
 * visitor is reading. Reviews we do not have yet — the first time a tab is opened —
 * become skeletons, because there is nothing to dim and an empty box for half a second
 * reads as "no reviews" rather than as "loading".
 *
 * Page one of the tab the visitor landed on was rendered by the server, so on a first
 * paint this shows real review text with no loading state at all.
 */
export default function ReviewList({
  tab,
  page,
  isLoading,
  hasFailed,
  isFiltered,
  perPage,
  source,
  onClearFilters,
}: Props) {
  const isProducts = tab === 'products';
  // The tab check matters: between clicking a tab and its first page arriving, `page`
  // still holds the other tab's reviews, and dimming those would look like they are
  // about to become magazines.
  const showing = page !== null && page.tab === tab && page.reviews.length > 0 ? page : null;

  if (isLoading && !showing) {
    return (
      <div
        aria-busy
        aria-live="polite"
        aria-label="Loading reviews"
        className="grid gap-4 xl:grid-cols-2"
      >
        <ReviewSkeleton count={Math.min(perPage, 4)} withProductStrip={isProducts} />
      </div>
    );
  }

  if (hasFailed && !showing) {
    return (
      <p className={MESSAGE_CLASS} role="status">
        We could not reach {source.name} just now. Please try again in a moment — you can also read
        every review on{' '}
        <a href={source.url} target="_blank" rel="noopener nofollow" className="text-primary underline">
          their site
        </a>
        .
      </p>
    );
  }

  if (!showing) {
    return (
      <p className={MESSAGE_CLASS} role="status">
        {isFiltered ? (
          <>
            No reviews match that search yet.{' '}
            <button
              type="button"
              onClick={onClearFilters}
              className="cursor-pointer border-0 bg-transparent p-0 text-sm text-primary underline"
            >
              Clear the filters
            </button>
          </>
        ) : (
          'Customer reviews are on their way — check back shortly.'
        )}
      </p>
    );
  }

  return (
    <div className="relative">
      {/*
        A failed page turn with reviews still on screen is the quiet failure to avoid: the
        pager moves to page 2, the spinner clears, and page 1 sits there at full opacity
        looking like the answer. Say so instead.
      */}
      {hasFailed && (
        <p
          role="status"
          className="mb-4 rounded-xl border border-solid border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          We could not reach {source.name} just now, so these are the reviews we already had. Please try
          again in a moment.
        </p>
      )}

      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-16 text-2xl text-primary">
          <Spinner />
          {/* The page's only loading indicator, here rather than on the control that was
              clicked: the tabs and the toolbar had their own, and each one appearing
              resized the row it sat in. */}
          <span role="status" className="sr-only">
            Loading reviews
          </span>
        </div>
      )}

      <div
        aria-busy={isLoading}
        className={cn(
          'grid gap-4 transition-opacity duration-150 xl:grid-cols-2',
          isLoading && 'pointer-events-none opacity-40'
        )}
      >
        {showing.reviews.map((review) =>
          isProducts && review.product ? (
            <ProductReviewCard key={review.id} review={review} source={source} />
          ) : (
            <ReviewCard key={review.id} review={review} source={source} />
          )
        )}
      </div>
    </div>
  );
}
